import { format, millisecondsToMinutes } from "date-fns";
import _ from "lodash";

export * from "./authority";
export * from "./request";
export * from "./date.utils";
export * from "./fileProcessing";
export * from "./leg-route-location";

export function valueIsEmpty(value) {
  if (!value) return true;

  if (value instanceof Object && Object.keys(value) === 0) {
    return true;
  }

  if (Array.isArray(value) && value.length === 0) {
    return true;
  }

  if (typeof value === "string" && value === "") {
    return true;
  }

  return false;
}

export function truncate(str, n, useWordBoundary) {
  if (str.length <= n) {
    return str;
  }
  const subString = str.slice(0, n - 1); // the original check
  return (
    (useWordBoundary
      ? subString.slice(0, subString.lastIndexOf(" "))
      : subString) + "..."
  );
}

export function capitalizeFirstLetter(string) {
  if (!string) return null;
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function capitalizeAddress(string) {
  if (!string) return null;
  return string.toUpperCase();
}

export function capitalizeAll(string) {
  if (!string) return null;
  return string.toUpperCase();
}

export function isObject(obj) {
  return typeof obj === "object" && !Array.isArray(obj) && obj !== null;
}

export function isNum(num) {
  return !isNaN(num);
}

export function createAlphabet() {
  const alpha = Array.from(Array(26)).map((e, i) => i + 65);
  const alphabet = alpha.map((x) => String.fromCharCode(x));
  return alphabet;
}

export function camelCaseToSpaces(str) {
  if (!str) return;
  return (
    str
      // Split camelCase without breaking acronyms (CDL, VIN, PM).
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
      .replace(/^./, function (first) {
        return first.toUpperCase();
      })
  );
}

export function renderArray(num, options) {
  const arr = Array.from(Array(num).keys()).map((key, i) => {
    if (options) {
      if (options.numbers) return key;
      if (options.letters) return String.fromCharCode(key + 64);
      if (options.item) return options.item(key, i);
    }
    return key;
  });
  return arr;
}

export function padInt(num, count = 2) {
  return num.toLocaleString("en-US", {
    minimumIntegerDigits: count,
    useGrouping: false,
  });
}

export function padTime(timeString, legacy = true) {
  const times = timeString.split(":");

  const formatted = times.map((time) => {
    return time.padStart(2, "0");
  });

  return formatted.join(":");
}

export function getFormattedTime(time) {
  if (!time) return "";

  if (time.includes("AM") || time.includes("PM")) {
    return time.replace(/^0/, "");
  }

  const milTime = time.split(":").join("");
  var hours24 = parseInt(milTime.substring(0, 2));
  var hours = ((hours24 + 11) % 12) + 1;
  var amPm = hours24 > 11 ? " PM" : " AM";
  var minutes = milTime.substring(2);

  return hours + ":" + minutes + amPm;
}

export function renderTimeDifference(start, end) {
  if (!start) return;
  if (!end) return;

  const startTime = start.split(":");
  const endTime = end.split(":");

  const s = new Date().setHours(...startTime);
  const e = new Date().setHours(...endTime);

  const diffMs = e - s; // milliseconds between now & end
  const diffHrs = Math.floor((diffMs % 86400000) / 3600000); // hours
  const diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000) / 60; // minutes
  const minsRounded = Math.round(diffMins * 100) / 100;

  const res = diffHrs + minsRounded;
  return res;
}

export function renderDayDifference(start, end, options) {
  if (!start) return;
  if (!end) return;

  const startDay = start.split("-");
  const endDay = end.split("-");

  const date1 = new Date(...startDay);
  const date2 = new Date(...endDay);

  const timeDiff = date2.getTime() - date1.getTime();
  const dayDiff = timeDiff / (1000 * 3600 * 24);

  return dayDiff;
}

export function parseFixed(num, fixed) {
  return Number(parseFloat(num).toFixed(fixed || 2));
}

export function checkNaN(num) {
  return num === "NaN" ? 0 : num;
}

export function renderAmount(amount) {
  const total = amount || 0;
  let parsed = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(total);

  return `${parsed}`;
}

export function formatDate(value, dbFormat, dateFns, time) {
  if (!value || value === "Invalid Date") return;
  const isUTCString = typeof value === "string" && value.endsWith("Z");
  if (typeof value === "string" && !dbFormat) {
    if (dateFns) {
      if (isUTCString) {
        const utcDate = new Date(value);
        const month = padInt(utcDate.getUTCMonth() + 1);
        const day = padInt(utcDate.getUTCDate());
        const year = utcDate.getUTCFullYear();
        return `${month}-${day}-${year}`;
      }
      return format(new Date(value), "MM/dd/yyyy").split("/").join("-");
    }

    if (time) {
      return format(new Date(value), "hh:mm a MM/dd/yyyy").split("/").join("-");
    }
    const newDate = value.substring(0, 10).split("-");

    const year = newDate[0].length !== 4 ? newDate[2] : newDate[0];
    const month = newDate[0].length !== 4 ? newDate[0] : newDate[1];
    const day = newDate[0].length !== 4 ? newDate[1] : newDate[2];

    return [month, day, year].join("-");
  }

  if (dateFns) {
    if (isUTCString) {
      const utcDate = new Date(value);
      const month = padInt(utcDate.getUTCMonth() + 1);
      const day = padInt(utcDate.getUTCDate());
      const year = utcDate.getUTCFullYear();
      return `${month}-${day}-${year}`;
    }
    return format(value || new Date(), "MM/dd/yyyy")
      .split("/")
      .join("-");
  }

  const newDate = value ? new Date(value) : new Date();

  // For date-only fields, use UTC methods ONLY for explicit UTC strings from the server
  // This ensures "2025-11-10T07:00:00.000Z" shows as 11-10, not 11-09
  // IMPORTANT: Do NOT auto-detect UTC based on date differences, as this breaks
  // user-selected local dates when the time crosses midnight UTC

  const year = isUTCString ? newDate.getUTCFullYear() : newDate.getFullYear();
  const month = isUTCString
    ? newDate.getUTCMonth() + 1
    : newDate.getMonth() + 1;
  const date = isUTCString ? newDate.getUTCDate() : newDate.getDate();

  if (dbFormat) {
    return `${year}-${month}-${date}`;
  }

  return `${month}-${date}-${year}`;
}

export function convertMonthToAbreviation(value) {
  let date = new Date(value);
  return `${
    ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][date.getDay()]
  } ${date.getDate()} ${
    [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ][date.getMonth()]
  }`;
}

export function getDPI() {
  var div = document.createElement("div");
  div.style.height = "1in";
  div.style.width = "1in";
  div.style.top = "-100%";
  div.style.left = "-100%";
  div.style.position = "absolute";

  document.body.appendChild(div);

  var result = div.offsetHeight;

  document.body.removeChild(div);

  return result;
}

export function removeEmptyFromObj(obj) {
  let newObj = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] === Object(obj[key]))
      newObj[key] = removeEmptyFromObj(obj[key]);
    else if (obj[key] !== undefined) newObj[key] = obj[key];
  });
  return newObj;
}

export function renderTotalMileage(mileage) {
  let res = checkNaN(Math.ceil(parseFixed(mileage)).toLocaleString("en-US"));

  if (!res) {
    res = 0;
  }
  return res;
}

export function zeroPad(num, spaces = 4, toAppend = "0") {
  return num.toString().padStart(spaces, toAppend);
}

export function camelize(str) {
  return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
    if (+match === 0) return ""; // or if (/\s+/.test(match)) for white spaces
    return index === 0 ? match.toLowerCase() : match.toUpperCase();
  });
}

Object.byString = function (o, s) {
  // Return undefined if the object is null/undefined
  if (o == null) {
    return;
  }

  s = s.replace(/\[(\w+)\]/g, ".$1"); // convert indexes to properties
  s = s.replace(/^\./, ""); // strip a leading dot
  var a = s.split(".");
  for (var i = 0, n = a.length; i < n; ++i) {
    var k = a?.[i];
    // The `in` operator throws TypeError on primitives (strings, numbers,
    // booleans). When an intermediate value along the dot-path is a
    // primitive (for example a cleared driver stored as ""), bail out and
    // return undefined instead of crashing the render.
    if (
      o != null &&
      (typeof o === "object" || typeof o === "function") &&
      k in o
    ) {
      o = o?.[k];
    } else {
      return;
    }
  }
  return o;
};

export function renderAddress(dataObj, id, point) {
  if (!dataObj) return;
  const data = id ? dataObj[id] : dataObj;
  const { address, name } = data;
  const capitalName = capitalizeAll(name);
  // const header = `${capitalizeFirstLetter(id)} `;
  const street = capitalizeAll(address.address);
  const addressRest = `${capitalizeAll(address.city)}, ${capitalizeAll(
    address.state,
  )} ${address.zipcode}`;
  // return capitalName;

  return (
    <tr className="text-left" colSpan={6.5}>
      {/* {header} */}
      {capitalName}
      <br />
      {street}
      <br />
      {addressRest}
    </tr>
  );
}

export function formatAddress(address) {
  if (!address) return "Address not available";

  if (typeof address === "object" && address !== null) {
    const {
      address: street,
      street: streetAlt,
      city,
      state,
      zipCode,
      zipcode,
    } = address;
    const parts = [street || streetAlt, city, state].filter(Boolean);
    const firstLine = parts.slice(0, 1).join(" ");
    const secondLine = `${parts.slice(1).join(", ")} ${
      zipCode || zipcode || ""
    }`.trim();

    return (
      <>
        {firstLine}
        {secondLine && <br />}
        {secondLine}
      </>
    );
  }

  if (typeof address === "string") {
    const parts = address.split(",");
    if (parts.length === 1) return address;
    return (
      <>
        {parts[0].trim()}
        <br />
        {parts.slice(1).join(",").trim()}
      </>
    );
  }

  return "Address not available";
}

export function renderAddressHeader(dataObj, id, point) {
  if (!dataObj) return;
  const data = id ? dataObj[id] : dataObj;
  const { name } = data;
  const capitalName = capitalizeAll(name);

  return (
    <tr className="text-left" colSpan={6.5}>
      {/* {header} */}
      {capitalName}
    </tr>
  );
}

/* 
  Format Phone number 
  formatPhoneNumber('2345678900')   => "(234) 567-8900"
  formatPhoneNumber('+12345678900') => "+1 (234) 567-8900"
*/
export function formatPhoneNumber(phoneNumberString) {
  var cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    var intlCode = match[1] ? "+1 " : "";
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
  }
  return null;
}

export function removeEmptyValues(obj) {
  return _.pickBy(obj, _.identity);
}

export function renderInitials(name) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("");
  return initials.length === 3 ? initials : initials.slice(0, 2);
}

export function ExcelDateToJSDate(excelDate) {
  if (!excelDate) return null;

  var utc_days = Math.floor(excelDate - 25569);
  var utc_value = utc_days * 86400;
  var date_info = new Date(utc_value * 1000);

  var fractional_day = excelDate - Math.floor(excelDate) + 0.0000001;
  var total_seconds = Math.floor(86400 * fractional_day);
  var seconds = total_seconds % 60;
  total_seconds -= seconds;

  var hours = Math.floor(total_seconds / (60 * 60));
  var minutes = Math.floor(total_seconds / 60) % 60;

  return new Date(
    date_info.getFullYear(),
    date_info.getMonth(),
    date_info.getDate(),
    hours,
    minutes,
    seconds,
  );
}

export const formatCurrency = (amount, options = {}) => {
  const {
    showSymbol = true,
    showZero = false,
    locale = "en-US",
    currency = "USD",
  } = options;

  // Handle null/undefined
  if (amount === null || amount === undefined) {
    return "";
  }

  // Handle zero values
  if (amount === 0 && !showZero) {
    return "";
  }

  // Convert to number if string
  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;

  // Check if valid number
  if (isNaN(numericAmount)) {
    return "";
  }

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: showSymbol ? "currency" : "decimal",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return formatter.format(numericAmount);
  } catch (error) {
    console.error("Error formatting currency:", error);
    return "";
  }
};

export const formatTimeWorked = (milliseconds) => {
  if (!milliseconds) return { display: "0 min", decimal: 0 };

  const totalMinutes = millisecondsToMinutes(milliseconds);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let display = "";
  if (hours > 0) {
    display += `${hours} hr${hours !== 1 ? "s" : ""} `;
  }
  if (minutes > 0 || hours === 0) {
    display += `${minutes} min`;
  }

  return {
    display: display.trim(),
    decimal: Number((totalMinutes / 60).toFixed(2)), // For calculations, returns hours as decimal
  };
};

/**
 * Formats a date/time string into a localized format
 * @param {string|Date} dateTime - The date/time to format
 * @param {string} [formatStr='MM/dd/yyyy hh:mm a'] - Optional format string
 * @returns {string} Formatted date/time string
 */
export const formatDateTime = (dateTime, formatStr = "MM/dd/yyyy hh:mm a") => {
  if (!dateTime) return "";

  try {
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return "";
    return format(date, formatStr);
  } catch (error) {
    console.error("Error formatting date/time:", error);
    return "";
  }
};
export function hoursToMilliseconds(hours) {
  return hours * 60 * 60 * 1000;
}

/**
 * Adds specified number of days to a date
 * @param {Date} date - The starting date
 * @param {number} days - Number of days to add
 * @returns {Date} New date with added days
 */
export const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const formatDateTimeRange = (start, end, options = {}) => {
  const {
    showSeconds = false,
    format24Hour = false,
    shortDate = true,
    separator = "-",
  } = options;

  if (!start) return "-";

  const startDate = new Date(start);
  const endDate = end ? new Date(end) : null;

  // Time formatting options
  const timeOptions = {
    hour: "numeric",
    minute: "numeric",
    ...(showSeconds && { second: "numeric" }),
    hour12: !format24Hour,
  };

  // Date formatting options
  const dateOptions = {
    month: shortDate ? "numeric" : "long",
    day: "numeric",
    year: "numeric",
  };

  // Format time strings
  const formatTimeStr = (date) => {
    return date.toLocaleTimeString(undefined, timeOptions);
  };

  // Format date string
  const formatDateStr = (date) => {
    return date.toLocaleDateString(undefined, dateOptions);
  };

  // If no end date, just show start date and time
  if (!endDate) {
    return `${formatDateStr(startDate)} ${formatTimeStr(startDate)}`;
  }

  // Check if same day
  const isSameDay = startDate.toDateString() === endDate.toDateString();

  if (isSameDay) {
    return `${formatDateStr(startDate)} ${formatTimeStr(
      startDate,
    )} ${separator} ${formatTimeStr(endDate)}`;
  }

  // Different days
  return `${formatDateStr(startDate)} ${formatTimeStr(
    startDate,
  )} ${separator} ${formatDateStr(endDate)} ${formatTimeStr(endDate)}`;
};
